import React, { useState, useEffect } from 'react';
import { X, Search, Loader, AlertCircle, CheckCircle, Link2, Plus } from 'lucide-react';
import pipedriveClient from '../services/pipedriveClient';

const RequestLinkDialog = ({ request, onClose, onSuccess }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateDeal, setShowCreateDeal] = useState(false);
  const [creatingDeal, setCreatingDeal] = useState(false);
  const [newDeal, setNewDeal] = useState({
    title: '',
    value: '',
    currency: 'EUR'
  });
  const [dealFields, setDealFields] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);

  // Helper function to create correct API URL
  const createApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    
    if (cleanBaseUrl.endsWith('/api')) {
      const endpointWithoutApi = cleanEndpoint.replace(/^api\//, '');
      return `${cleanBaseUrl}/${endpointWithoutApi}`;
    }
    
    return `${cleanBaseUrl}/${cleanEndpoint}`;
  };

  // Search for deals in Pipedrive (exactly like DealViewer.js)
  const searchDeals = async (query) => {
    if (!query || query.length < 2) {
      setDeals([]);
      return;
    }

    setSearching(true);
    setError(null);

    try {
      const response = await pipedriveClient.get('/deals/search', { params: { term: query } });

      if (response.data && response.data.data && response.data.data.items) {
        const dealItems = response.data.data.items.map(item => ({
          id: item.item.id,
          title: item.item.title,
          organization: item.item.org_name,
          value: item.item.value,
          currency: item.item.currency
        }));
        setDeals(dealItems);
      } else {
        setDeals([]);
      }
    } catch (err) {
      console.error('Error searching deals:', err);
      setError('Fehler beim Suchen von Deals');
      setDeals([]);
    } finally {
      setSearching(false);
    }
  };

  // Handle deal selection
  const handleDealSelect = (deal) => {
    setSelectedDeal(deal);
    setSearchTerm(deal.title);
    setDeals([]);
  };

  // Create new deal in Pipedrive
  const handleCreateDeal = async () => {
    if (!newDeal.title?.trim()) {
      setError('Bitte geben Sie einen Deal-Titel ein');
      return;
    }

    setCreatingDeal(true);
    setError(null);

    try {
      // Build deal data with all fields
      const dealData = {};
      
      // Standard fields
      if (newDeal.title) dealData.title = newDeal.title;
      if (newDeal.value) dealData.value = parseFloat(newDeal.value);
      if (newDeal.currency) dealData.currency = newDeal.currency;
      if (newDeal.pipeline_id) dealData.pipeline_id = parseInt(newDeal.pipeline_id);
      if (newDeal.stage_id) dealData.stage_id = parseInt(newDeal.stage_id);
      if (newDeal.user_id) dealData.user_id = parseInt(newDeal.user_id);
      
      // Add all custom fields from dealFields
      dealFields.forEach(field => {
        const fieldKey = field.key;
        const fieldValue = newDeal[fieldKey];
        
        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
          // Handle different field types
          if (field.field_type === 'date') {
            // Date fields should be in YYYY-MM-DD format
            dealData[fieldKey] = fieldValue;
          } else if (field.field_type === 'number' || field.field_type === 'monetary') {
            dealData[fieldKey] = parseFloat(fieldValue);
          } else if (field.field_type === 'set' && Array.isArray(fieldValue)) {
            // Multi-select fields
            dealData[fieldKey] = fieldValue;
          } else {
            dealData[fieldKey] = fieldValue;
          }
        }
      });

      // Add customer information from request (if not already set)
      if (!dealData['07c3da8804f7b96210e45474fba35b8691211ddd'] && request.origin_address) {
        dealData['07c3da8804f7b96210e45474fba35b8691211ddd'] = request.origin_address; // Auszugsadresse
      }
      if (!dealData['9cb4de1018ec8404feeaaaf7ee9b293c78c44281'] && request.destination_address) {
        dealData['9cb4de1018ec8404feeaaaf7ee9b293c78c44281'] = request.destination_address; // Einzugsadresse
      }
      if (!dealData['b9d01d5dcd86c878a57cb0febd336e4d390af900'] && request.move_date) {
        dealData['b9d01d5dcd86c878a57cb0febd336e4d390af900'] = request.move_date; // Wunschdatum
      }

      const response = await pipedriveClient.post('/deals', dealData);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Fehler beim Erstellen des Deals');
      }

      const createdDeal = response.data.data;
      console.log('Deal created successfully:', createdDeal);

      // Automatically link the request to the newly created deal
      await handleLinkToDeal(createdDeal.id.toString());
      
      // Close create deal form
      setShowCreateDeal(false);
      setNewDeal({ title: '', value: '', currency: 'EUR' });
      setSelectedPipeline(null);
    } catch (err) {
      console.error('Error creating deal:', err);
      setError(err.response?.data?.error?.message || err.message || 'Fehler beim Erstellen des Deals');
    } finally {
      setCreatingDeal(false);
    }
  };

  // Link request to deal (internal function)
  const handleLinkToDeal = async (dealId) => {
    setLinking(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const url = createApiUrl(`api/requests/${request.id}/link`);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deal_id: dealId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Verknüpfen');
      }

      const data = await response.json();
      console.log('Request linked successfully:', data);
      
      if (onSuccess) {
        // Pass inspection_id and deal_id to onSuccess callback
        onSuccess(data);
      }
    } catch (err) {
      console.error('Error linking request:', err);
      setError(err.message || 'Fehler beim Verknüpfen der Anfrage');
      throw err; // Re-throw to handle in calling function
    } finally {
      setLinking(false);
    }
  };

  // Link request to deal (public function)
  const handleLink = async () => {
    if (!selectedDeal) {
      setError('Bitte wählen Sie einen Deal aus');
      return;
    }

    await handleLinkToDeal(selectedDeal.id.toString());
  };

  // Load Pipedrive fields, pipelines, and users when showing create deal form
  useEffect(() => {
    if (showCreateDeal) {
      loadPipedriveData();
    }
  }, [showCreateDeal]);

  // Load stages when pipeline changes
  useEffect(() => {
    if (selectedPipeline) {
      loadStages(selectedPipeline);
    } else {
      setStages([]);
    }
  }, [selectedPipeline]);

  const loadPipedriveData = async () => {
    setLoadingFields(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Nicht authentifiziert. Bitte melden Sie sich an.');
        setLoadingFields(false);
        return;
      }
      
      // Load deal fields
      const fieldsUrl = createApiUrl('api/pipedrive/deal-fields');
      console.log('Loading deal fields from:', fieldsUrl);
      const fieldsResponse = await fetch(fieldsUrl, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Check if response is HTML (error page)
      const contentType = fieldsResponse.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        const text = await fieldsResponse.text();
        console.error('Received HTML instead of JSON:', text.substring(0, 200));
        setError('Server-Fehler: Endpunkt nicht gefunden. Bitte überprüfen Sie die Server-Konfiguration.');
        setLoadingFields(false);
        return;
      }
      
      if (fieldsResponse.ok) {
        const fieldsData = await fieldsResponse.json();
        console.log('Loaded deal fields:', fieldsData.data?.length || 0, fieldsData);
        setDealFields(fieldsData.data || []);
      } else {
        const errorText = await fieldsResponse.text().catch(() => 'Unknown error');
        console.error('Error loading deal fields:', fieldsResponse.status, errorText);
        try {
          const errorData = JSON.parse(errorText);
          const errorMsg = errorData.error || errorData.message || fieldsResponse.statusText;
          setError(`Fehler beim Laden der Deal-Felder: ${errorMsg}`);
        } catch {
          setError(`Fehler beim Laden der Deal-Felder: ${fieldsResponse.status} ${fieldsResponse.statusText}`);
        }
        // Don't continue loading other data if deal fields fail
        setLoadingFields(false);
        return;
      }

      // Load pipelines
      const pipelinesUrl = createApiUrl('api/pipedrive/pipelines');
      console.log('Loading pipelines from:', pipelinesUrl);
      const pipelinesResponse = await fetch(pipelinesUrl, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (pipelinesResponse.ok) {
        const pipelinesData = await pipelinesResponse.json();
        console.log('Loaded pipelines:', pipelinesData.data?.length || 0);
        setPipelines(pipelinesData.data || []);
      } else {
        const errorText = await pipelinesResponse.text().catch(() => 'Unknown error');
        console.error('Error loading pipelines:', pipelinesResponse.status, errorText);
      }

      // Load users
      const usersUrl = createApiUrl('api/pipedrive/users');
      console.log('Loading users from:', usersUrl);
      const usersResponse = await fetch(usersUrl, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        console.log('Loaded users:', usersData.data?.length || 0);
        setUsers(usersData.data || []);
      } else {
        const errorText = await usersResponse.text().catch(() => 'Unknown error');
        console.error('Error loading users:', usersResponse.status, errorText);
      }
    } catch (err) {
      console.error('Error loading Pipedrive data:', err);
      setError(`Fehler beim Laden der Pipedrive-Daten: ${err.message}`);
    } finally {
      setLoadingFields(false);
    }
  };

  const loadStages = async (pipelineId) => {
    try {
      const token = localStorage.getItem('token');
      const stagesUrl = createApiUrl(`api/pipedrive/stages/${pipelineId}`);
      const response = await fetch(stagesUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const stagesData = await response.json();
        setStages(stagesData.data || []);
      }
    } catch (err) {
      console.error('Error loading stages:', err);
    }
  };

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchDeals(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Anfrage mit Deal verknüpfen</h2>
            <p className="text-sm text-gray-600 mt-1">
              {request.customer_name} • {request.origin_address} → {request.destination_address}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Schließen"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Request Summary */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h3 className="font-semibold text-gray-900 mb-2">Anfrage-Zusammenfassung</h3>
            <div className="space-y-1 text-sm text-gray-700">
              <div><span className="font-medium">Kunde:</span> {request.customer_name}</div>
              <div><span className="font-medium">E-Mail:</span> {request.customer_email}</div>
              <div><span className="font-medium">Volumen:</span> {request.total_volume?.toFixed(2) || '0.00'} m³</div>
              <div><span className="font-medium">Räume:</span> {request.total_rooms || 0}</div>
            </div>
          </div>

          {/* Deal Search or Create */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Pipedrive Deal suchen
              </label>
              <button
                onClick={() => {
                  setShowCreateDeal(!showCreateDeal);
                  setError(null);
                  if (!showCreateDeal) {
                    // Pre-fill deal with customer information
                    const initialDeal = {
                      title: `Umzug ${request.customer_name}`,
                      value: '',
                      currency: 'EUR',
                      '07c3da8804f7b96210e45474fba35b8691211ddd': request.origin_address || '', // Auszugsadresse
                      '9cb4de1018ec8404feeaaaf7ee9b293c78c44281': request.destination_address || '', // Einzugsadresse
                    };
                    if (request.move_date) {
                      initialDeal['b9d01d5dcd86c878a57cb0febd336e4d390af900'] = request.move_date; // Wunschdatum
                    }
                    setNewDeal(initialDeal);
                  } else {
                    // Reset when closing
                    setNewDeal({ title: '', value: '', currency: 'EUR' });
                    setSelectedPipeline(null);
                  }
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {showCreateDeal ? 'Suche verwenden' : 'Neuen Deal erstellen'}
              </button>
            </div>

            {!showCreateDeal ? (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Deal-Titel oder Organisation eingeben..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-gray-900">Neuen Deal erstellen</h4>
                
                {loadingFields && (
                  <div className="flex items-center justify-center py-4">
                    <Loader className="h-5 w-5 animate-spin text-green-600" />
                    <span className="ml-2 text-sm text-gray-600">Lade Pipedrive-Felder...</span>
                  </div>
                )}

                {!loadingFields && dealFields.length === 0 && pipelines.length === 0 && users.length === 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      ⚠️ Pipedrive-Felder konnten nicht geladen werden. Bitte überprüfen Sie die Browser-Konsole (F12) für Details.
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Stellen Sie sicher, dass Sie angemeldet sind und der Pipedrive API Token korrekt konfiguriert ist.
                    </p>
                  </div>
                )}

                {/* Debug Info - Always show in development */}
                {!loadingFields && (
                  <div className="mb-2 text-xs text-gray-500">
                    <p>Geladen: {dealFields.length} Felder, {pipelines.length} Pipelines, {users.length} Benutzer</p>
                  </div>
                )}

                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {/* Standard Fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Deal-Titel <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newDeal.title || ''}
                      onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
                      placeholder="z.B. Umzug Max Mustermann"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Wert (optional)
                      </label>
                      <input
                        type="number"
                        value={newDeal.value || ''}
                        onChange={(e) => setNewDeal({ ...newDeal, value: e.target.value })}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Währung
                      </label>
                      <select
                        value={newDeal.currency || 'EUR'}
                        onChange={(e) => setNewDeal({ ...newDeal, currency: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="CHF">CHF</option>
                      </select>
                    </div>
                  </div>

                  {/* Pipeline */}
                  {pipelines.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pipeline
                      </label>
                      <select
                        value={newDeal.pipeline_id || ''}
                        onChange={(e) => {
                          const pipelineId = e.target.value;
                          setNewDeal({ ...newDeal, pipeline_id: pipelineId, stage_id: '' });
                          setSelectedPipeline(pipelineId);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">Keine Pipeline</option>
                        {pipelines.map(pipeline => (
                          <option key={pipeline.id} value={pipeline.id}>
                            {pipeline.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Stage */}
                  {stages.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stage
                      </label>
                      <select
                        value={newDeal.stage_id || ''}
                        onChange={(e) => setNewDeal({ ...newDeal, stage_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">Keine Stage</option>
                        {stages.map(stage => (
                          <option key={stage.id} value={stage.id}>
                            {stage.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Verantwortlicher (User) */}
                  {users.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Verantwortlicher
                      </label>
                      <select
                        value={newDeal.user_id || ''}
                        onChange={(e) => setNewDeal({ ...newDeal, user_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">Kein Verantwortlicher</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Address and Date Fields (if defined in Pipedrive) */}
                  {dealFields
                    .filter(field => {
                      const addressFields = [
                        '07c3da8804f7b96210e45474fba35b8691211ddd', // Auszugsadresse
                        '9cb4de1018ec8404feeaaaf7ee9b293c78c44281', // Einzugsadresse
                        'b9d01d5dcd86c878a57cb0febd336e4d390af900'  // Wunschdatum
                      ];
                      return addressFields.includes(field.key);
                    })
                    .map(field => {
                      const fieldKey = field.key;
                      const fieldValue = newDeal[fieldKey] || 
                        (fieldKey === '07c3da8804f7b96210e45474fba35b8691211ddd' ? request.origin_address : '') ||
                        (fieldKey === '9cb4de1018ec8404feeaaaf7ee9b293c78c44281' ? request.destination_address : '') ||
                        (fieldKey === 'b9d01d5dcd86c878a57cb0febd336e4d390af900' ? request.move_date : '');

                      return (
                        <div key={fieldKey}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.name}
                            <span className="text-xs text-gray-500 ml-1">(aus Anfrage)</span>
                          </label>
                          <input
                            type={field.field_type === 'date' ? 'date' : 'text'}
                            value={fieldValue}
                            onChange={(e) => setNewDeal({ ...newDeal, [fieldKey]: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            title="Wird automatisch aus der Anfrage übernommen (bearbeitbar)"
                          />
                        </div>
                      );
                    })}

                  {/* Debug Info */}
                  {process.env.NODE_ENV === 'development' && (
                    <div className="text-xs text-gray-500 p-2 bg-gray-100 rounded">
                      <p>Debug: {dealFields.length} Felder, {pipelines.length} Pipelines, {users.length} Benutzer geladen</p>
                    </div>
                  )}

                  {/* Custom Fields - Show important ones first */}
                  {dealFields.length > 0 && (
                    <div className="pt-2 border-t border-green-200">
                      <h5 className="text-sm font-semibold text-gray-700 mb-3">Weitere Felder ({dealFields.filter(f => {
                        const standardFields = ['title', 'value', 'currency', 'pipeline_id', 'stage_id', 'user_id'];
                        const addressFields = ['07c3da8804f7b96210e45474fba35b8691211ddd', '9cb4de1018ec8404feeaaaf7ee9b293c78c44281', 'b9d01d5dcd86c878a57cb0febd336e4d390af900'];
                        return !standardFields.includes(f.key) && !addressFields.includes(f.key);
                      }).length})</h5>
                    </div>
                  )}
                  {dealFields
                    .filter(field => {
                      // Exclude standard fields and already handled fields
                      const standardFields = ['title', 'value', 'currency', 'pipeline_id', 'stage_id', 'user_id'];
                      const addressFields = [
                        '07c3da8804f7b96210e45474fba35b8691211ddd', // Auszugsadresse
                        '9cb4de1018ec8404feeaaaf7ee9b293c78c44281', // Einzugsadresse
                        'b9d01d5dcd86c878a57cb0febd336e4d390af900'  // Wunschdatum
                      ];
                      // Show all editable fields
                      return !standardFields.includes(field.key) && !addressFields.includes(field.key);
                    })
                    .sort((a, b) => {
                      // Sort: required fields first, then by name
                      if (a.required_flag && !b.required_flag) return -1;
                      if (!a.required_flag && b.required_flag) return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map(field => {
                      const fieldKey = field.key;
                      const fieldValue = newDeal[fieldKey] || '';

                      return (
                        <div key={fieldKey}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.name}
                            {field.required_flag && <span className="text-red-500"> *</span>}
                          </label>
                          {field.field_type === 'set' ? (
                            // Multi-select
                            <select
                              multiple
                              value={Array.isArray(fieldValue) ? fieldValue : []}
                              onChange={(e) => {
                                const selected = Array.from(e.target.selectedOptions, option => option.value);
                                setNewDeal({ ...newDeal, [fieldKey]: selected });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              size={Math.min(field.options?.length || 3, 5)}
                            >
                              {field.options?.map(option => (
                                <option key={option.id} value={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : field.field_type === 'enum' ? (
                            // Single select
                            <select
                              value={fieldValue}
                              onChange={(e) => setNewDeal({ ...newDeal, [fieldKey]: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            >
                              <option value="">-- Bitte wählen --</option>
                              {field.options?.map(option => (
                                <option key={option.id} value={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : field.field_type === 'date' ? (
                            // Date
                            <input
                              type="date"
                              value={fieldValue}
                              onChange={(e) => setNewDeal({ ...newDeal, [fieldKey]: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                          ) : field.field_type === 'number' || field.field_type === 'monetary' ? (
                            // Number
                            <input
                              type="number"
                              value={fieldValue}
                              onChange={(e) => setNewDeal({ ...newDeal, [fieldKey]: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                          ) : (
                            // Text
                            <input
                              type="text"
                              value={fieldValue}
                              onChange={(e) => setNewDeal({ ...newDeal, [fieldKey]: e.target.value })}
                              placeholder={field.name}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                          )}
                        </div>
                      );
                    })}
                </div>

                <div className="text-xs text-gray-600 pt-2 border-t border-green-200">
                  <p>Die Adressen und das Umzugsdatum werden automatisch aus der Anfrage übernommen.</p>
                </div>
                <button
                  onClick={handleCreateDeal}
                  disabled={creatingDeal || !newDeal.title?.trim()}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {creatingDeal ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      Wird erstellt...
                    </>
                  ) : (
                    <>
                      <Plus className="h-5 w-5" />
                      Deal erstellen und verknüpfen
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Search Results */}
            {!showCreateDeal && deals.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-60 overflow-auto">
                {deals.map((deal) => (
                  <button
                    key={deal.id}
                    onClick={() => handleDealSelect(deal)}
                    className={`w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors ${
                      selectedDeal?.id === deal.id ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{deal.title}</div>
                    {deal.organization && (
                      <div className="text-sm text-gray-600">{deal.organization}</div>
                    )}
                    {deal.value && (
                      <div className="text-sm text-gray-500">
                        {deal.value} {deal.currency || 'EUR'}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {!showCreateDeal && searchTerm && deals.length === 0 && !searching && (
              <div className="mt-2 text-sm text-gray-500 text-center py-4">
                Keine Deals gefunden
              </div>
            )}
          </div>

          {/* Selected Deal */}
          {!showCreateDeal && selectedDeal && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold text-gray-900">Ausgewählter Deal</h3>
              </div>
              <div className="space-y-1 text-sm text-gray-700">
                <div><span className="font-medium">Titel:</span> {selectedDeal.title}</div>
                {selectedDeal.organization && (
                  <div><span className="font-medium">Organisation:</span> {selectedDeal.organization}</div>
                )}
                {selectedDeal.value && (
                  <div><span className="font-medium">Wert:</span> {selectedDeal.value} {selectedDeal.currency || 'EUR'}</div>
                )}
                <div><span className="font-medium">Deal-ID:</span> {selectedDeal.id}</div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Info */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">
              <strong>Hinweis:</strong> Nach der Verknüpfung wird die Anfrage in eine normale Inspektion umgewandelt 
              und erscheint in der Deal-Liste. Die Anfrage wird aus der Liste der offenen Anfragen entfernt.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            disabled={linking || creatingDeal}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          {!showCreateDeal && (
            <button
              onClick={handleLink}
              disabled={!selectedDeal || linking}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {linking ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  Verknüpfe...
                </>
              ) : (
                <>
                  <Link2 className="h-5 w-5" />
                  Verknüpfen
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestLinkDialog;

